import { useEffect } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import { KyrgyzstanDetailContent } from 'widgets/kyrgyzstan/detail-content'
import { KyrgyzstanDetailShell } from 'widgets/kyrgyzstan/detail-shell'

import {
  clearSearchFormState,
  hasCompleteKyrgyzstanBookingParams,
  kyrgyzstanDetailSelectionStore,
  restoreSearchFormState,
  saveSearchFormState,
} from 'entities/kyrgyzstan'
import {
  getEffectiveStayParams,
  getKyrgyzstanGalleryRouteState,
  getKyrgyzstanMapRouteState,
  hasKyrgyzstanDetailSearchParams,
} from 'entities/kyrgyzstan/lib'
import { getDestinationTitle } from 'entities/kyrgyzstan/lib/get-destination-title'
import { getKyrgyzstanDetailSubtitle } from 'entities/kyrgyzstan/lib/get-kyrgyzstan-detail-subtitle'
import {
  kyrgyzstanDetailContextStore,
  kyrgyzstanDetailDataStore,
  kyrgyzstanSearchStore,
} from 'entities/kyrgyzstan/model'
import { getShareUrl } from 'entities/tour/ui/appeal-details/libs/getShareUrl'
import userStore from 'entities/user/module/user-store'

import { checkUserAuth } from 'shared/lib/checkUserAuth'
import { getRedirectUrl } from 'shared/lib/getRedirectUrl'
import { ButtonContinue } from 'shared/ui/bottom-continue'

const KyrgyzstanDetailPage = observer(() => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>() as { id: string }
  const { t } = useTranslation()
  const user = userStore.getAccountInfo()

  const {
    detailSearchParams,
    prepareContextForParent,
    saveContext,
    setDetailSearchParams,
  } = kyrgyzstanDetailContextStore

  const {
    parentDetail,
    childrenList,
    fetchParentDetail,
    fetchChildrenList,
    resetChildrenList,
    isParentLoading,
    isChildrenListLoading,
    hasChildrenListLoaded,
    parentError,
  } = kyrgyzstanDetailDataStore

  const currentParentDetail = parentDetail?.id === id ? parentDetail : null
  const hasChildren = Boolean(currentParentDetail?.type.has_children)
  const shouldShowBookingButton = Boolean(currentParentDetail && !hasChildren)
  const shouldShowNoOffersButton = Boolean(
    currentParentDetail &&
    hasChildren &&
    hasChildrenListLoaded &&
    !isChildrenListLoading &&
    !childrenList.length,
  )
  const effectiveStayParams = getEffectiveStayParams({
    searchParams: detailSearchParams,
    realty: currentParentDetail,
  })
  const canBook = hasCompleteKyrgyzstanBookingParams(effectiveStayParams)

  useEffect(() => {
    if (user) {
      restoreSearchFormState(kyrgyzstanSearchStore)
      clearSearchFormState()
    }
  }, [user])

  useEffect(() => {
    prepareContextForParent(id)
  }, [id, prepareContextForParent])

  useEffect(() => {
    fetchParentDetail(id)
  }, [id, fetchParentDetail])

  useEffect(() => {
    if (!hasChildren) {
      resetChildrenList()

      return undefined
    }

    fetchChildrenList(
      id,
      getEffectiveStayParams({
        searchParams: detailSearchParams,
        realty: currentParentDetail,
      }) ?? undefined,
    )

    return resetChildrenList
  }, [
    id,
    hasChildren,
    detailSearchParams,
    currentParentDetail,
    fetchChildrenList,
    resetChildrenList,
  ])

  const handleGoBack = () => {
    const hasPreviousPages =
      window.history.length > 2 &&
      (document.referrer.includes(window.location.origin) ||
        window.history.length > 3)

    if (hasPreviousPages) {
      navigate(-1)
    } else {
      navigate(RoutingPaths.kyrgyzstan.path())
    }
  }

  const handleOpenGallery = (parentId: string) => {
    if (currentParentDetail?.id !== parentId) return

    navigate(RoutingPaths.kyrgyzstan.gallery.path(parentId), {
      state: getKyrgyzstanGalleryRouteState(currentParentDetail),
    })
  }

  const handleOpenFullMap = (parentId: string) => {
    if (currentParentDetail?.id !== parentId) return

    navigate(RoutingPaths.kyrgyzstan.map.path(parentId), {
      state: getKyrgyzstanMapRouteState(currentParentDetail),
    })
  }

  const handleOpenBookingStep = async (navigatePath: string) => {
    if (!currentParentDetail) return

    if (
      effectiveStayParams &&
      !hasKyrgyzstanDetailSearchParams(detailSearchParams)
    ) {
      setDetailSearchParams(effectiveStayParams)
    }

    const redirectUrl = getRedirectUrl(navigatePath)

    kyrgyzstanDetailSelectionStore.selectParent(currentParentDetail)

    await checkUserAuth({
      redirectUrl,
      onSuccess: () => {
        clearSearchFormState()

        navigate(navigatePath)
      },
      onError: () => {
        saveContext()
        kyrgyzstanDetailSelectionStore.saveSelection()
        saveSearchFormState(kyrgyzstanSearchStore)
      },
    })
  }

  const handleBook = async () => {
    if (!canBook) return

    await handleOpenBookingStep(RoutingPaths.kyrgyzstan.booking.path())
  }

  const handleSelectOption = async (optionId: string) => {
    const selectedChild = childrenList.find((item) => item.id === optionId)

    if (!currentParentDetail || !selectedChild) return

    const navigatePath = RoutingPaths.kyrgyzstan.optionDetail.path(optionId)
    const redirectUrl = getRedirectUrl(navigatePath)

    kyrgyzstanDetailSelectionStore.selectParent(currentParentDetail)
    kyrgyzstanDetailSelectionStore.selectChild(selectedChild)

    await checkUserAuth({
      redirectUrl,
      onSuccess: () => {
        clearSearchFormState()

        navigate(navigatePath)
      },
      onError: () => {
        saveContext()
        kyrgyzstanDetailSelectionStore.saveSelection()
        saveSearchFormState(kyrgyzstanSearchStore)
      },
    })
  }

  const shareUrl = getShareUrl(RoutingPaths.kyrgyzstan.detail.path(id))
  const detailError =
    parentError ||
    (!currentParentDetail ? t('kyrgyzstan_block.error.title') : null)
  const detailSubtitle = getKyrgyzstanDetailSubtitle({
    searchParams: effectiveStayParams,
    t,
  })

  return (
    <KyrgyzstanDetailShell
      header={{
        title: currentParentDetail
          ? getDestinationTitle(currentParentDetail)
          : undefined,
        subtitle: detailSubtitle,
        onGoBack: handleGoBack,
      }}
      share={
        currentParentDetail
          ? {
              url: shareUrl,
              text: currentParentDetail.title,
            }
          : undefined
      }
      state={{
        isLoading: isParentLoading,
        error: detailError,
      }}
      bottomAction={
        (shouldShowBookingButton || shouldShowNoOffersButton) && (
          <ButtonContinue
            disabled={!canBook || shouldShowNoOffersButton}
            onClick={handleBook}
          >
            <span className="semi-bold-17">
              {shouldShowNoOffersButton
                ? t('kyrgyzstan_block.detail.no_offers_for_dates')
                : t('kyrgyzstan_block.detail.book_button')}
            </span>
          </ButtonContinue>
        )
      }
    >
      {currentParentDetail && (
        <KyrgyzstanDetailContent
          realty={currentParentDetail}
          stayParams={effectiveStayParams}
          childrenList={childrenList}
          isChildrenListLoading={isChildrenListLoading}
          childrenTitle={t('kyrgyzstan_block.detail.choose_option_title')}
          onOpenDates={() =>
            handleOpenBookingStep(
              RoutingPaths.kyrgyzstan.calendarBooking.path(),
            )
          }
          onOpenGuests={() =>
            handleOpenBookingStep(
              RoutingPaths.kyrgyzstan.selectGuestsBooking.path(),
            )
          }
          onOpenGallery={handleOpenGallery}
          onOpenMap={handleOpenFullMap}
          onSelectOption={handleSelectOption}
        />
      )}
    </KyrgyzstanDetailShell>
  )
})

export default KyrgyzstanDetailPage
